import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { ItemExprUtils } from '../../../../utils/item-expr.utils';

import { EChartsOption } from 'echarts';

import { LoadingController } from '../../../../helpers/loading.controller';
import { ToastService } from '../../../../services/toast.service';
import {
  combineLatest,
  finalize,
  Subject,
  Subscription,
  takeUntil,
  tap,
  switchMap,
  catchError,
  EMPTY,
  merge,
  map,
  filter,
  mergeMap,
} from 'rxjs';
import { ExportService } from '../../../../services/export.service';
import { ColorUtils } from '../../../../utils/color.utils';

import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import LegendWidget from '@arcgis/core/widgets/Legend';
import { EsriUtils } from 'app/shared/utils/esri.utils';
import IdentifyParameters from '@arcgis/core/rest/support/IdentifyParameters';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import {
  geomFieldsIgnored,
  systemFieldTypesIgnored,
} from 'app/shared/constants/ignore-fields.constant';
import { AppConfigService } from 'app/shared/services/app-config.service';
import { LayersInfoService } from '../../layers.service';
import { ArrayUtils } from 'app/shared/utils/array.utils';
import { QuotaWidgetConfigLike } from 'app/shared/interfaces/app-config.interface';
import { ScreenService } from 'app/shared/services/screen.service';
import { TranslationService } from '@services/translation.service';

@Component({
  selector: 'app-quota-info',
  templateUrl: 'quota-info.component.html',
  styleUrls: ['quota-info.component.scss'],
})
export class QuotaInfoComponent implements OnChanges, OnDestroy {
  @Input()
  config: QuotaWidgetConfigLike;
  @Input()
  mapView: __esri.MapView;
  @Input()
  quotaGroupLayer: __esri.GroupLayer;

  fields: any[];

  topItems: any[] = [];

  private _itemsResults: any[] = [];
  private get itemsResults() {
    return this._itemsResults;
  }

  private set itemsResults(value) {
    this._itemsResults = value;
    this.topItems = this._itemsResults?.slice(0, this.config?.topLimit ?? 5);
  }

  @Input()
  columnAlias: string;
  @Input()
  initialMsg: string;
  @Input()
  noDataMsg: string = 'Nenhum dado encontrado';

  infos: { label: string; value: string }[];

  barChartOptions: EChartsOption;

  loadingController = new LoadingController();
  initialState = true;

  private exportFileName: string;

  private configSubscription: Subscription;

  isMobileModeObservable = this.screenService.observeIsMobile();

  private lifecycle = new Subject<void>();

  constructor(
    private appConfig: AppConfigService,
    private mainMapService: MainMapService,
    private layersInfoService: LayersInfoService,
    private exportService: ExportService,
    private toastService: ToastService,
    private screenService: ScreenService,
    private ts: TranslationService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.config || changes.mapView || changes.quotaGroupLayer) {
      this.handleConfig();
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();

    this.configSubscription?.unsubscribe();
  }

  onBtnExportClick(e) {
    if (this.loadingController.isShown() || !this.itemsResults?.length) {
      return;
    }

    this.loadingController.show();
    this.exportService
      .exportAsXlsxLink({
        fields: this.fields,
        features: this.itemsResults.map((attributes) => {
          return { attributes };
        }),
        fileName: this.exportFileName,
      })
      .pipe(
        finalize(() => {
          this.loadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (link) => {
          window.open(link, '_blank');
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-export-data']
          );
        },
      });
  }

  extractDisplayName = (item: any, index: number) => {
    return ItemExprUtils.extractValue(
      item,
      index,
      this.config?.displayFieldName
    );
  };

  extractPercent = (item: any, index: number) => {
    return ItemExprUtils.extractValue(
      item,
      index,
      this.config?.percentFieldName
    );
  };

  private handleConfig() {
    this.configSubscription?.unsubscribe();
    if (!this.mapView || !this.config) {
      return;
    }

    const subscription = new Subscription();

    this.mainMapService.disableMapPopup();

    const { mapView } = this;
    const quotaGroupLayer: __esri.LayersMixin =
      this.quotaGroupLayer ?? mapView.map;

    const config = this.config;
    const mapServerUrl = this.appConfig.normalizeUrl(config.mapServerUrl);

    const percentFielName = config.percentFieldName;

    const quotaImageLayer = new MapImageLayer({
      url: mapServerUrl,
    });

    let legendWidget: LegendWidget;

    this.loadingController.show();
    const loadLayersObservable = EsriUtils.loadAllLayers(quotaImageLayer).pipe(
      mergeMap(() => {
        return this.layersInfoService.getLayerTitle(mapServerUrl).pipe(
          tap((title) => {
            if (title) {
              quotaImageLayer.title = title;
            }

            quotaGroupLayer.add(quotaImageLayer);

            legendWidget = new LegendWidget({
              headingLevel: 2,
              view: mapView,
              layerInfos: [
                {
                  layer: quotaImageLayer,
                  sublayerIds: [
                    config.layerLegendIdDistrito,
                    config.layerLegendIdConcelho,
                    config.layerLegendIdFreguesia,
                  ],
                },
              ],
            });
            mapView.ui.add(legendWidget, 'bottom-left');
          })
        );
      }),
      tap(() => {
        this.loadingController.hide();
      })
    );

    const layerIds = [
      config.layerIdFreguesia,
      config.layerIdConcelho,
      config.layerIdDistrito,
    ];

    subscription.add(
      combineLatest(
        merge(
          EsriUtils.leftClicks(mapView),
          this.mainMapService.onRequestClear.pipe(
            map(() => {
              this.fields = [];
              this.infos = [];
              this.itemsResults = [];
              this.initialState = true;

              return null;
            })
          )
        ),
        loadLayersObservable
      )
        .pipe(
          switchMap(([e]) => {
            if (!e) {
              return EMPTY;
            }

            this.exportFileName = (
              [
                [config.layerIdFreguesia, config.exportFileNameFreguesia],
                [config.layerIdConcelho, config.exportFileNameConcelho],
                [config.layerIdDistrito, config.exportFileNameDistrito],
              ] as [number, string][]
            ).find(([layerId]) => {
              const layer = quotaImageLayer.findSublayerById(layerId);
              if (layer && EsriUtils.isLayerInScale(layer, mapView.scale)) {
                return true;
              }

              return false;
            })?.[1];

            this.loadingController.show();

            const parameters = new IdentifyParameters({
              tolerance: 3,
              width: mapView.width,
              height: mapView.height,
              layerOption: 'visible',
              layerIds,
              geometry: e.mapPoint,
              mapExtent: mapView.extent,
              spatialReference: mapView.spatialReference,
              returnGeometry: false,
            });

            return EsriUtils.identify(mapServerUrl, parameters).pipe(
              catchError(() => {
                return EMPTY;
              }),
              finalize(() => {
                this.loadingController.hide();
              })
            );
          })
        )
        .subscribe((res) => {
          const results: any[] =
            res?.results
              ?.map((x) => x?.feature?.attributes)
              ?.filter((x) => !!x)
              ?.sort((a, b) => {
                return a[percentFielName] === b[percentFielName]
                  ? 0
                  : a[percentFielName] > b[percentFielName]
                  ? -1
                  : 1;
              }) ?? [];

          const firstObj = results[0];

          const infos = [
            { label: 'Distrito', key: 'distrito' },
            { label: 'Concelho', key: 'concelho' },
            { label: 'Freguesia', key: 'freguesia' },
          ]
            .map(({ label, key }) => {
              if (!(firstObj && key in firstObj)) {
                return null;
              }

              const value = firstObj[key];
              return { label, value };
            })
            .filter((x) => !!x);

          let fields = !res?.results?.length
            ? []
            : layerIds.flatMap((layerId) => {
                const layer = quotaImageLayer.findSublayerById(layerId);
                return layer?.fields ?? [];
              });

          fields = ArrayUtils.uniqBy(fields, (x) => x?.name);

          const fieldsToIgnore = fields.filter((field) => {
            if (
              this.config?.displayFieldName === field.name ||
              this.config?.percentFieldName === field.name
            ) {
              return false;
            }

            return (
              geomFieldsIgnored.includes(field.name) ||
              systemFieldTypesIgnored.includes(field.type)
            );
          });

          fields = fields.filter(
            (field) => !fieldsToIgnore.some((x) => x.name === field.name)
          );

          for (const item of results) {
            for (const field of fieldsToIgnore) {
              delete item[field.name];
            }
          }

          this.fields = fields;
          this.infos = infos;
          this.itemsResults = results;

          this.initialState = false;
          this.setupChart();
        })
    );

    subscription.add(() => {
      if (legendWidget) {
        mapView.ui.remove(legendWidget);
        legendWidget.destroy();
      }

      quotaGroupLayer.remove(quotaImageLayer);
      quotaImageLayer.destroy();

      this.mainMapService.enableMapPopup();
    });

    this.configSubscription = subscription;
  }

  private setupChart() {
    const items = this.topItems ?? [];
    const labels = items.map((item, index) =>
      this.extractDisplayName(item, index)
    );
    const data = items.map((item, index) => this.extractPercent(item, index));

    const colors = ColorUtils.generateColorRange(
      [
        'rgba(8, 82, 156, 1)',
        'rgba(49, 131, 189, 1)',
        'rgba(107, 175, 214, 1)',
        'rgba(190, 216, 232, 1)',
        'rgba(240, 244, 255, 1)',
      ],
      data.length
    );

    const fontFamily =
      "'Roboto', 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif";
    const fontSize = 8;

    this.barChartOptions = {
      series: [
        {
          type: 'bar',
          data: data.map((value, index) => {
            return {
              value,
              itemStyle: {
                color: colors[index],
              },
            };
          }),
        },
      ],
      xAxis: {
        type: 'category',
        boundaryGap: true,
        data: labels,
        splitArea: {
          show: true,
        },
        splitLine: {
          show: true,
        },
        axisLabel: {
          fontFamily,
          fontSize,
          align: 'right',
          rotate: 52,
          hideOverlap: true,
          showMinLabel: true,
          showMaxLabel: true,
          formatter: (value, index) => {
            const newValue = value.substring(0, 19);

            if (newValue.length < value.length) {
              value = newValue + '...';
            }

            return value;
          },
        },
        axisPointer: {
          snap: true,
        },
        axisTick: {
          alignWithLabel: true,
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          fontFamily,
          fontSize,
        },
      },
      grid: {
        top: 10,
        left: 0,
        right: 1,
        bottom: 0,
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        triggerOn: 'mousemove',
        textStyle: {
          fontFamily,
          fontSize: 12,
        },
      },
      textStyle: {
        fontFamily,
        fontSize,
      },
    };
  }
}
